import React, { useCallback, useEffect } from 'react';
import { FieldError, Text, TextField, TextArea as RacTextArea, TextAreaProps, useContextProps, TextAreaContext } from 'react-aria-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';

import { InputLabel } from '../../../elements/input-label';
import { TextBlock } from '../../../elements/text-block';

import './index.scss';

type InputProps = {
  leading?: React.ReactNode;
  iconLeading?: IconDefinition;
  iconTrailing?: IconDefinition;
} & TextAreaProps;

const TextArea = React.forwardRef((props: InputProps, ref: React.Ref<HTMLTextAreaElement>) => {
  const { leading, iconLeading, iconTrailing, onInput, ...restProps } = props;
  const [textareaProps, textareaRef] = useContextProps(restProps, ref, TextAreaContext);

  const autoResize = useCallback((textarea: HTMLTextAreaElement) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  const handleInput = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      autoResize(e.currentTarget);
      if (onInput) {
        onInput(e);
      }
    },
    [autoResize, onInput],
  );

  // Auto-resize on initial render and when value changes
  useEffect(() => {
    if (textareaRef.current) {
      autoResize(textareaRef.current);
    }
  }, [textareaProps.value, restProps.value, autoResize, textareaRef]);

  // Auto-resize on window resize
  useEffect(() => {
    const handleResize = () => {
      if (textareaRef.current) {
        autoResize(textareaRef.current);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [autoResize, textareaRef]);

  // Focus input when clicking on the wrapper
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      textareaRef.current?.focus();
    },
    [textareaRef],
  );

  return (
    <div className="rac-input-field-input-wrapper" onClick={handleClick}>
      {leading}
      {iconLeading && <FontAwesomeIcon icon={iconLeading} className={`myd-input--leading-icon`} aria-hidden />}
      <div className="rac-input-wrapper">
        <RacTextArea {...textareaProps} ref={textareaRef} onInput={handleInput} rows={1} />
      </div>
      {iconTrailing && <FontAwesomeIcon icon={iconTrailing} className={`myd-input--trailing-icon`} aria-hidden />}
    </div>
  );
});

type InputFieldProps = {
  label: string;
  description?: string;
  errorMessage?: string;
  tipTitle?: string;
  tipDescription?: string;
  isReadOnly?: boolean;
} & InputProps;

export const InputTextArea = (props: InputFieldProps) => {
  const { label, description, errorMessage, isReadOnly, tipTitle, tipDescription, ...inputProps } = props;

  return (
    <TextField isInvalid={Boolean(errorMessage)} isReadOnly={isReadOnly}>
      <InputLabel asterisk={props.required} description={description} tipTitle={tipTitle} tipDescription={tipDescription}>
        {label}
      </InputLabel>
      <TextArea inputMode="text" required {...inputProps} />
      {description && (
        <Text slot="description">
          <TextBlock variant="XXS-regular-hint-text">{description}</TextBlock>
        </Text>
      )}
      <FieldError>
        <TextBlock variant="XXS-regular-error-message">{errorMessage}</TextBlock>
      </FieldError>
    </TextField>
  );
};

export default InputTextArea;
